/**
 * ChairmanDailyReportsPage
 * Chairman view of all HR daily reports.
 * Features: date-range filter, HR filter, status filter, paginated ReportList
 * with inline FeedbackBox per card.
 */
import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/Layout/MainLayout';
import ReportList from '../components/DailyReport/organisms/ReportList';
import dailyReportService from '../services/dailyReportService';
import attendanceService from '../services/attendanceService';
import {
  MdAssignment,
  MdFilterList,
  MdRefresh,
  MdPendingActions,
  MdCheckCircle,
  MdGroup
} from 'react-icons/md';

const INPUT_CLS =
  'px-3 py-2 rounded-lg border border-border bg-background text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-green/40 focus:border-accent-green transition-all duration-150';

const ChairmanDailyReportsPage = () => {
  // Filter state
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    hrId: '',
    status: ''
  });

  // Data state
  const [reports, setReports]       = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading]       = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // HR employees list (for dropdown)
  const [hrList, setHrList] = useState([]);

  // Stats (derived from pagination + local state)
  const [stats, setStats] = useState({ total: 0, pending: 0, reviewed: 0 });

  // ── Load HR list ──────────────────────────────────────────────────────────
  useEffect(() => {
    attendanceService.getHREmployeesList()
      .then((res) => {
        if (res.success) setHrList(res.data || []);
      })
      .catch(() => {});
  }, []);

  // ── Fetch reports ─────────────────────────────────────────────────────────
  const fetchReports = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const res = await dailyReportService.getAllReports({
        ...filters,
        page,
        limit: 15
      });
      if (res.success) {
        setReports(res.data.items);
        setPagination(res.data.pagination);
        // Compute stats from this page for display (approximate)
        const allItems = res.data.items;
        setStats({
          total:    res.data.pagination.total,
          pending:  allItems.filter((r) => r.status === 'pending').length,
          reviewed: allItems.filter((r) => r.status === 'reviewed').length
        });
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    fetchReports(currentPage);
  }, [fetchReports, currentPage]);

  // ── Feedback saved → patch report in local list ───────────────────────────
  const handleFeedbackSaved = (updated) => {
    setReports((prev) =>
      prev.map((r) => (r._id === updated._id ? updated : r))
    );
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ fromDate: '', toDate: '', hrId: '', status: '' });
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent-green/10 flex items-center justify-center flex-shrink-0">
              <MdAssignment size={22} className="text-accent-green" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary tracking-tight">HR Daily Reports</h1>
              <p className="text-sm text-text-secondary">Review and give feedback on all HR submissions.</p>
            </div>
          </div>

          <button
            onClick={() => fetchReports(currentPage)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm text-text-secondary hover:bg-background transition-colors"
          >
            <MdRefresh size={16} /> Refresh
          </button>
        </div>

        {/* ── Stats Row ── */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard icon={MdGroup}          value={stats.total}    label="Total Reports"  color="text-accent-green"   bg="bg-accent-green/10"     />
          <StatCard icon={MdPendingActions} value={stats.pending}  label="Pending"        color="text-status-warning" bg="bg-status-warning-bg"   />
          <StatCard icon={MdCheckCircle}    value={stats.reviewed} label="Reviewed"       color="text-status-success" bg="bg-status-success-bg"   />
        </div>

        {/* ── Filters ── */}
        <div className="bg-surface rounded-2xl border border-border-light shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <MdFilterList size={18} className="text-accent-green" />
            <h2 className="text-sm font-semibold text-text-primary">Filters</h2>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="ml-auto text-xs text-status-error hover:underline font-medium"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* From Date */}
            <div>
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1.5 block">
                From Date
              </label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className={INPUT_CLS + ' w-full'}
              />
            </div>

            {/* To Date */}
            <div>
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1.5 block">
                To Date
              </label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className={INPUT_CLS + ' w-full'}
              />
            </div>

            {/* HR Employee */}
            <div>
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1.5 block">
                HR Employee
              </label>
              <select
                name="hrId"
                value={filters.hrId}
                onChange={handleFilterChange}
                className={INPUT_CLS + ' w-full'}
              >
                <option value="">All HR</option>
                {hrList.map((hr) => (
                  <option key={hr._id} value={hr._id}>
                    {hr.name || hr.fullName || hr.username || hr.phoneNumber || hr.email || 'HR Employee'}
                    {hr.employeeId ? ` (${hr.employeeId})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1.5 block">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className={INPUT_CLS + ' w-full'}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── Report List ── */}
        <div className="bg-surface rounded-2xl border border-border-light shadow-sm p-6">
          <ReportList
            reports={reports}
            pagination={pagination}
            onPageChange={(p) => setCurrentPage(p)}
            isChairman
            onFeedbackSaved={handleFeedbackSaved}
            loading={loading}
          />
        </div>

      </div>
    </MainLayout>
  );
};

const StatCard = ({ icon: Icon, value, label, color, bg }) => (
  <div className={`rounded-xl p-4 ${bg} border border-transparent flex flex-col gap-1`}>
    <Icon size={20} className={color} />
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    <p className="text-xs font-medium text-text-secondary">{label}</p>
  </div>
);

export default ChairmanDailyReportsPage;
